'use client';
import { createContext, useContext, ReactNode } from 'react';
import { useFilesStore } from '@/stores';

interface FileNode {
  id: string;
  name: string;
  type: 'file' | 'folder';
  folderId: string | null;
  children?: FileNode[];
}

interface FileItem {
  folderID: string | number;
  filePath: string;
  metadata?: {
    title?: string;
    keywords?: string[];
    summary?: string;
  };
}

interface ProjectFilesCache {
  fileTree: FileNode[];
  isLoaded: boolean;
  isLoading: boolean;
}

interface FilesContextType {
  // State
  projectFilesCache: Record<string, ProjectFilesCache>;
  fileNames: Record<string, any>; // Keep for backward compatibility
  files: FileItem[];
  totalItems: number;
  isFilesLoading: boolean;
  // Actions
  loadProjectFiles: (projectId: string) => Promise<void>;
  getProjectFiles: (projectId: string) => FileNode[];
  loadFiles: (
    page: number,
    pageSize: number | null,
    projectId: string | null,
    condition?: any
  ) => Promise<void>;
  resetProjectFilesCache: (projectId?: string) => void;
  setFiles: (files: FileItem[], totalItems: number) => void;
}
const FilesContext = createContext<FilesContextType | undefined>(undefined);
interface FilesProviderProps {
  children: ReactNode;
}
export function FilesProvider({ children }: FilesProviderProps) {
  const state = useFilesStore();
  return (
    <FilesContext.Provider value={state}>{children}</FilesContext.Provider>
  );
}
// eslint-disable-next-line react-refresh/only-export-components
export function useFiles() {
  const context = useContext(FilesContext);
  if (context === undefined) {
    throw new Error('useFiles must be used within a FilesProvider');
  }
  return context;
}
